import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { MAX_REDACTION_ANNOTATIONS, MAX_REDACTION_REASON_LENGTH } from 'citadel-shared/redactionLimits.js'
import { EDIT_REDACTIONS_SKIP_CODES } from 'citadel-shared/redactionEditing.js'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined'
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

import axios from '../../../dataClient/dataClient'
import Button, { SecondaryButton, DangerButton, IconOnlyButton } from '../../common/Button'
import Loading from '../../common/Loading'
import LoadingError from '../../common/LoadingError'
import { theme } from '../../../themes/theme'
import { AI_BOX_FILL, AI_BOX_STROKE, withAlpha } from '../../styles/colors'
import { useInterval } from '../../../hooks/useInterval'
import { useVisibilityChange } from '../../../hooks/useVisibilityChange'
import {
  useWorkflowPoll,
  WorkflowPollTimeoutError,
  WorkflowPollCancelledError,
  DEFAULT_WORKFLOW_POLL_INTERVAL_MS
} from '../../../hooks/redaction/useWorkflowPoll'
import ContinuousPdfPages from './ContinuousPdfPages'
import RedactionLayer from './RedactionLayer'
import UnsavedChangesDialog from './UnsavedChangesDialog'
import RedactAllOccurrencesDialog from './RedactAllOccurrencesDialog'
import DocumentSearchToolbar from './DocumentSearchToolbar'
import DocumentSearchHighlights from './DocumentSearchHighlights'
import ZoomPageToolbar from './ZoomPageToolbar'
import usePdfDocument from '../../../hooks/redaction/usePdfDocument'
import usePageParam from '../../../hooks/redaction/usePageParam'
import usePageZoom from '../../../hooks/redaction/usePageZoom'
import useUnsavedGuard from '../../../hooks/redaction/useUnsavedGuard'
import useRedactAllOccurrences from '../../../hooks/redaction/useRedactAllOccurrences'
import useDocumentSearch from '../../../hooks/redaction/useDocumentSearch'
import { backToRequestUrl } from './paths'
import { boxesToPayload, newBoxId, serializeBoxes } from '../../../util/redaction/annotationMath'
import { getRedactionReasonPlaceholder } from '../../../util/redaction/redactionReasonPlaceholder'
import {
  Shell,
  HeaderBar,
  HeaderTitles,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  ActionTooltip,
  UnsavedHint,
  Toolbar,
  ToolbarLayout,
  ToolbarLeft,
  ToolbarCenter,
  ToolbarRight,
  ToolGroup,
  ToolButton,
  Body,
  CanvasScroll,
  Legend,
  LegendSwatch,
  Rail,
  RailTitle,
  RailSectionTitle,
  RailDivider,
  KeyValueRow,
  HelperText,
  ReasonTextarea,
  BoxList,
  BoxListItem,
  BoxListButton,
  BoxReason,
  EmptyStateCard,
  EmptyStateIcon,
  EmptyStateTitle,
  FileSummaryCard,
  InfoBand,
  InfoBandText,
  InfoBandDetail,
  WarningBand,
  EditorErrorText,
  BandIcon
} from './editorChrome'

export const STALE_VERSION_MESSAGE = 'This file was changed by someone else — reload to continue.'

function isTypingTarget(target) {
  const tag = target?.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable
}

const isStaleVersionError = e => e?.response?.status === 409 && e.response.data?.code === EDIT_REDACTIONS_SKIP_CODES.STALE_VERSION

// Edit redaction boxes and persist them through an EDIT_REDACTIONS workflow.
function RedactionEditorPage({ pollIntervalMs = DEFAULT_WORKFLOW_POLL_INTERVAL_MS }) {
  const { foiId, categoryId } = useParams()
  const navigate = useNavigate()
  const { poll } = useWorkflowPoll({ intervalMs: pollIntervalMs })
  const pageVisible = useVisibilityChange()

  const [category, setCategory] = useState(null)
  const [notedFile, setNotedFile] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [pendingWorkflow, setPendingWorkflow] = useState(null)

  const {
    pages,
    boxes: loadedBoxes,
    loading: pdfLoading,
    error: pdfError,
    version,
    reload
  } = usePdfDocument(notedFile?.id, {
    readAnnotations: true
  })

  const [boxes, setBoxes] = useState([])
  const [savedSnapshot, setSavedSnapshot] = useState('[]')
  const [savedIds, setSavedIds] = useState(() => new Set())
  const [editCount, setEditCount] = useState(0)
  const [selectedId, setSelectedId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState(null)
  const [staleVersion, setStaleVersion] = useState(false)
  const pageCount = pages.length
  const clearSelection = useCallback(() => setSelectedId(null), [])
  const page = usePageParam(pageCount, { onChange: clearSelection })
  const documentSearch = useDocumentSearch({ pages, pageNavigation: page })
  const pageWidthPoints = pages.length ? Math.max(...pages.map(pdfPage => pdfPage.getViewport({ scale: 1 }).width)) : 612
  const zoom = usePageZoom({ pageWidthPoints, pdfLoading })

  const dirty = useMemo(() => serializeBoxes(boxes) !== savedSnapshot, [boxes, savedSnapshot])
  const selected = boxes.find(b => b.id === selectedId) ?? null

  const fetchCategory = useCallback(async () => {
    const { data } = await axios.get(`/category/${categoryId}`)
    setCategory(data.data)
    return data.data
  }, [categoryId])

  const fetchNotedFile = useCallback(async () => {
    const { data } = await axios.get(`/category/${categoryId}/files`)
    const files = data.data.files ?? []
    const noted = files.find(f => f.status === 'REDACTIONS_NOTED' && !f.archivedAt)
    setNotedFile(noted ?? null)
    return noted ?? null
  }, [categoryId])

  const fetchLatestWorkflow = useCallback(async () => {
    const { data } = await axios.get(`/category/${categoryId}/workflow`)
    const workflow = data.data
    setPendingWorkflow(workflow && workflow.status === 'PENDING' ? workflow : null)
    return workflow
  }, [categoryId])

  const reloadFile = useCallback(async () => {
    await Promise.all([fetchCategory(), fetchNotedFile()])
    await reload()
  }, [fetchCategory, fetchNotedFile, reload])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const loadedCategory = await fetchCategory()
        if (cancelled) return
        if (loadedCategory.foiId && loadedCategory.foiId !== foiId) {
          setLoadError(['This category does not belong to the requested FOI request.'])
          return
        }
        const noted = await fetchNotedFile()
        if (cancelled) return
        if (!noted) {
          setLoadError(['This category has no redactions-noted PDF to edit. Suggest redactions first.'])
          return
        }
        await fetchLatestWorkflow()
      } catch (e) {
        if (cancelled) return
        console.error('RedactionEditorPage: failed to load', e)
        setLoadError([
          e.response?.status === 404
            ? 'The requested category does not exist.'
            : 'There was an error loading the redaction editor. Please refresh the page to try again.'
        ])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [foiId, categoryId, fetchCategory, fetchNotedFile, fetchLatestWorkflow])

  // Keep controls synchronized with workflows started elsewhere.
  useInterval(
    async () => {
      try {
        const workflow = await fetchLatestWorkflow()
        if (!workflow || workflow.status !== 'PENDING') {
          await fetchCategory()
          await fetchNotedFile()
        }
      } catch (e) {
        console.error('RedactionEditorPage: workflow poll failed', e)
      }
    },
    pendingWorkflow && pageVisible ? pollIntervalMs : null
  )

  const busy = saving || applying || Boolean(pendingWorkflow) || staleVersion
  const redactAll = useRedactAllOccurrences({
    foiId,
    categoryId,
    pages,
    boxes,
    selected,
    dirty,
    busy,
    baseVersion: notedFile?.contentUpdatedAt,
    maxBoxes: MAX_REDACTION_ANNOTATIONS,
    poll,
    reloadFile,
    onStaleVersion: () => setStaleVersion(true)
  })
  const anyBusy = busy || redactAll.redacting

  useEffect(() => {
    if (version === 0) return
    setBoxes(loadedBoxes)
    setSavedSnapshot(serializeBoxes(loadedBoxes))
    setSavedIds(new Set(loadedBoxes.map(b => b.id)))
    setEditCount(0)
    setSelectedId(null)
    setStaleVersion(false)
    const selectedGeometry = redactAll.consumeReload(loadedBoxes)
    if (selectedGeometry) {
      const sameBox = loadedBoxes.find(
        b =>
          b.page === selectedGeometry.page &&
          Math.abs(b.x - selectedGeometry.x) < 0.5 &&
          Math.abs(b.y - selectedGeometry.y) < 0.5 &&
          Math.abs(b.w - selectedGeometry.w) < 0.5 &&
          Math.abs(b.h - selectedGeometry.h) < 0.5
      )
      if (sameBox) setSelectedId(sameBox.id)
    }
  }, [version, loadedBoxes])

  const bump = () => setEditCount(c => c + 1)

  const handleCreate = useCallback(box => {
    const created = { ...box, id: newBoxId(), reason: '', origin: 'manual' }
    setBoxes(prev => [...prev, created])
    setSelectedId(created.id)
    bump()
  }, [])

  const handleUpdate = useCallback((id, patch) => {
    setBoxes(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)))
    bump()
  }, [])

  function handleReasonChange(id, reason) {
    setBoxes(prev => prev.map(b => (b.id === id ? { ...b, reason } : b)))
  }

  function handleDelete(id) {
    if (!id) return
    setBoxes(prev => prev.filter(b => b.id !== id))
    setSelectedId(null)
    bump()
  }

  function handleDiscard() {
    setBoxes(JSON.parse(JSON.stringify(loadedBoxes)))
    setEditCount(0)
    setSelectedId(null)
    setSaveError(null)
  }

  useEffect(() => {
    function onKeyDown(e) {
      if (isTypingTarget(e.target)) return
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !anyBusy) {
        e.preventDefault()
        handleDelete(selectedId)
      } else if (e.key === 'Escape') {
        setSelectedId(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedId, anyBusy])

  async function handleSave() {
    if (!dirty || anyBusy) return false
    setSaving(true)
    setSaveError(null)
    try {
      const { data } = await axios.post(`/category/${categoryId}/redactions`, {
        annotations: boxesToPayload(boxes, pages),
        baseVersion: notedFile.contentUpdatedAt
      })
      const workflow = await poll(data.data.workflowId)
      if (workflow.status !== 'SUCCEEDED') {
        setSaveError(`Saving failed${workflow.errorMessage ? `: ${workflow.errorMessage}` : ''}. Your edits are still here — try again.`)
        return false
      }
      await reloadFile()
      return true
    } catch (e) {
      if (e instanceof WorkflowPollCancelledError) return false
      if (isStaleVersionError(e)) {
        console.info('RedactionEditorPage: the noted file changed since it was loaded (409 STALE_VERSION)')
        setStaleVersion(true)
        return false
      }
      console.error('RedactionEditorPage: save failed', e)
      const message = e instanceof WorkflowPollTimeoutError ? 'the server did not finish in time' : e.response?.data?.message
      setSaveError(`Saving failed${message ? `: ${message}` : ''}. Your edits are still here — try again.`)
      return false
    } finally {
      setSaving(false)
    }
  }

  async function handleApply() {
    if (dirty || anyBusy) return
    setApplying(true)
    setApplyError(null)
    try {
      await axios.post(`/category/${categoryId}/applyRedactions`)
      navigate(backToRequestUrl(foiId, categoryId))
    } catch (e) {
      console.error('RedactionEditorPage: apply failed', e)
      setApplyError('Could not start applying redactions. Try again.')
      setApplying(false)
    }
  }

  async function handleStaleReload() {
    setSaveError(null)
    try {
      await reloadFile()
    } catch (e) {
      console.error('RedactionEditorPage: reload after a stale version failed', e)
      setSaveError('Could not reload the file. Please refresh the page.')
    }
  }

  const guard = useUnsavedGuard({ dirty, onSave: handleSave, onDiscard: handleDiscard })

  if (loadError) {
    return (
      <Shell>
        <HeaderBar>
          <IconOnlyButton aria-label="Back to request" onClick={() => navigate(backToRequestUrl(foiId, categoryId))}>
            <ArrowBackIcon />
          </IconOnlyButton>
          <HeaderTitles>
            <HeaderTitle>{category ? `${category.name} — redactions` : 'Redaction editor'}</HeaderTitle>
          </HeaderTitles>
        </HeaderBar>
        <LoadingError message={loadError} />
      </Shell>
    )
  }

  if (!category || !notedFile || (pdfLoading && version === 0)) {
    return (
      <Shell>
        <Loading />
      </Shell>
    )
  }

  if (pdfError && version === 0) {
    return (
      <Shell>
        <LoadingError message={['The redactions-noted PDF could not be opened. Please refresh the page to try again.']} />
      </Shell>
    )
  }

  const onThisPage = boxes.filter(b => b.page === page.pageIndex)
  const suggestedCount = boxes.filter(b => b.origin !== 'manual').length
  const manualCount = boxes.filter(b => b.origin === 'manual').length
  const deletedSinceSave = [...savedIds].filter(id => !boxes.some(b => b.id === id)).length
  const canApply = !dirty && !anyBusy && category.status === 'REDACTIONS_NOTED'
  const applyTitle = dirty
    ? 'Save your changes before applying redactions'
    : category.status !== 'REDACTIONS_NOTED'
      ? 'Redactions can be applied once the category is back at Redactions Noted'
      : pendingWorkflow
        ? 'Another workflow is running for this category'
        : 'Burn the saved redactions into a new PDF'
  const lastSaved = notedFile.contentUpdatedAt ? format(new Date(notedFile.contentUpdatedAt), 'MMM-dd-yyyy hh:mm a') : null
  const redactAllDetail = redactAll.summary?.details.join(' ') ?? ''
  const redactAllTitle = dirty
    ? 'Save your changes before redacting all occurrences'
    : pendingWorkflow
      ? 'Another workflow is running for this category'
      : 'Find every occurrence of a text in this file and add a redaction box for each'
  const unredactAllTitle = dirty
    ? 'Save your changes before un-redacting all occurrences'
    : pendingWorkflow
      ? 'Another workflow is running for this category'
      : 'Find every occurrence of a text and remove only its matching area from existing redactions'
  const subtitle = `${notedFile.filename} · ${pageCount} ${pageCount === 1 ? 'page' : 'pages'} · ${suggestedCount} suggested · ${manualCount} manual`
  const manualFill = withAlpha(theme.colors.primary, 0.25)
  const SummaryBand = redactAll.summary?.warning ? WarningBand : InfoBand

  return (
    <Shell data-testid="redaction-editor">
      <HeaderBar>
        <IconOnlyButton aria-label="Back to request" onClick={() => navigate(backToRequestUrl(foiId, categoryId))}>
          <ArrowBackIcon />
        </IconOnlyButton>
        <HeaderTitles>
          <HeaderTitle title={`${category.name} — redactions`}>{category.name} — redactions</HeaderTitle>
          <HeaderSubtitle data-testid="editor-subtitle" title={subtitle}>
            {subtitle}
          </HeaderSubtitle>
        </HeaderTitles>
        <HeaderActions>
          {dirty && <UnsavedHint data-testid="unsaved-hint">Unsaved changes</UnsavedHint>}
          <SecondaryButton onClick={handleDiscard} disabled={!dirty || saving} padding="short">
            Discard
          </SecondaryButton>
          <Button onClick={handleSave} disabled={!dirty || anyBusy} loading={saving} padding="short">
            Save
          </Button>
          <ActionTooltip title={applyTitle}>
            <SecondaryButton onClick={handleApply} disabled={!canApply} loading={applying} padding="short" data-testid="apply-redactions">
              <FactCheckOutlinedIcon /> Apply Redactions
            </SecondaryButton>
          </ActionTooltip>
        </HeaderActions>
      </HeaderBar>

      <Toolbar>
        <ToolbarLayout>
          <ToolbarLeft>
            <DocumentSearchToolbar
              open={documentSearch.isOpen}
              query={documentSearch.query}
              searching={documentSearch.isSearching}
              searched={documentSearch.hasSearched}
              resultCount={documentSearch.results.length}
              activeIndex={documentSearch.activeIndex}
              onOpen={documentSearch.open}
              onClose={documentSearch.close}
              onQueryChange={documentSearch.updateQuery}
              onSearch={documentSearch.search}
              onPrevious={documentSearch.showPrevious}
              onNext={documentSearch.showNext}
            />
          </ToolbarLeft>
          <ToolbarCenter>
            <ZoomPageToolbar zoom={zoom} page={page} pageCount={pageCount} />
          </ToolbarCenter>
          <ToolbarRight>
            <ToolGroup role="group" aria-label="Occurrence actions">
              <ActionTooltip title={redactAllTitle}>
                <ToolButton onClick={redactAll.openRedactDialog} disabled={dirty || anyBusy} data-testid="redact-all-occurrences">
                  <FindReplaceOutlinedIcon /> Redact all
                </ToolButton>
              </ActionTooltip>
              <ActionTooltip title={unredactAllTitle}>
                <ToolButton onClick={redactAll.openUnredactDialog} disabled={dirty || anyBusy} data-testid="unredact-all-occurrences">
                  <RemoveCircleOutlineIcon /> Un-redact all
                </ToolButton>
              </ActionTooltip>
            </ToolGroup>
          </ToolbarRight>
        </ToolbarLayout>
      </Toolbar>

      {staleVersion && (
        <WarningBand role="alert" data-testid="stale-version-band">
          <BandIcon>
            <WarningAmberOutlinedIcon />
          </BandIcon>
          <InfoBandText>
            <span>{STALE_VERSION_MESSAGE}</span>
            <InfoBandDetail>Your unsaved edits here will be discarded when you reload.</InfoBandDetail>
          </InfoBandText>
          <SecondaryButton onClick={handleStaleReload} padding="short" data-testid="stale-version-reload">
            <RefreshIcon /> Reload
          </SecondaryButton>
        </WarningBand>
      )}
      {(saveError || applyError || (redactAll.error && !redactAll.isOpen)) && (
        <EditorErrorText role="alert">{saveError || applyError || redactAll.error}</EditorErrorText>
      )}
      {redactAll.summary && (
        <SummaryBand
          role="status"
          data-testid="redact-all-summary"
          data-truncated={redactAll.summary.truncated ? 'true' : 'false'}
          data-warning={redactAll.summary.warning ? 'true' : 'false'}
          data-ocr-skipped-pages={String(redactAll.summary.ocrSkippedPages ?? 0)}>
          {redactAll.summary.warning && (
            <BandIcon>
              <WarningAmberOutlinedIcon />
            </BandIcon>
          )}
          <InfoBandText>
            <span>{redactAll.summary.lead} </span>
            {redactAllDetail && <InfoBandDetail>{redactAllDetail}</InfoBandDetail>}
          </InfoBandText>
          <IconOnlyButton aria-label="Dismiss" onClick={redactAll.dismissSummary}>
            <CloseIcon />
          </IconOnlyButton>
        </SummaryBand>
      )}

      <Body>
        <CanvasScroll ref={zoom.scrollRef} data-testid="editor-canvas">
          <ContinuousPdfPages
            pages={pages}
            scale={zoom.scale}
            pageIndex={page.pageIndex}
            navigationKey={page.navigationKey}
            scrollRef={zoom.scrollRef}
            onPageChange={page.setVisiblePage}
            renderOverlay={(pageIndex, dims) => (
              <>
                <RedactionLayer
                  pageIndex={pageIndex}
                  dims={dims}
                  boxes={boxes}
                  tool="add"
                  editable={!anyBusy}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onCreate={handleCreate}
                  onUpdate={handleUpdate}
                />
                <DocumentSearchHighlights
                  matches={documentSearch.results.map((result, index) => ({ ...result, index })).filter(result => result.page === pageIndex)}
                  activeIndex={documentSearch.activeIndex}
                  scale={dims.scale}
                />
              </>
            )}
          />
          <Legend>
            <span>
              <LegendSwatch fill={AI_BOX_FILL} stroke={AI_BOX_STROKE} />
              Suggested (AI)
            </span>
            <span>
              <LegendSwatch fill={manualFill} stroke={theme.colors.primary} />
              Manual
            </span>
            <span>
              <LegendSwatch fill={manualFill} stroke={theme.colors.primary} selected />
              Selected
            </span>
          </Legend>
        </CanvasScroll>

        <Rail as="aside" data-testid="editor-rail">
          {selected ? (
            <>
              <RailTitle>Selected redaction</RailTitle>
              <KeyValueRow>
                <span>Page</span>
                <span>
                  {selected.page + 1} of {pageCount}
                </span>
              </KeyValueRow>
              <KeyValueRow>
                <span>Source</span>
                <span data-testid="selected-source">{selected.origin === 'manual' ? 'Manual' : 'AI suggestion'}</span>
              </KeyValueRow>
              <RailSectionTitle as="label" htmlFor="redaction-reason">
                Reason (free text)
              </RailSectionTitle>
              <ReasonTextarea
                id="redaction-reason"
                key={selected.id}
                value={selected.reason}
                maxLength={MAX_REDACTION_REASON_LENGTH}
                disabled={anyBusy}
                onChange={e => handleReasonChange(selected.id, e.target.value)}
                onBlur={() => {
                  if (serializeBoxes(boxes) !== savedSnapshot) bump()
                }}
                placeholder={getRedactionReasonPlaceholder()}
              />
              <HelperText>Printed next to this box in the reasons list when redactions are applied.</HelperText>
              <DangerButton onClick={() => handleDelete(selected.id)} disabled={anyBusy} padding="short">
                <DeleteOutlineIcon /> Delete box
              </DangerButton>
            </>
          ) : (
            <>
              <RailTitle>Redactions</RailTitle>
              <EmptyStateCard data-testid="empty-selection">
                <EmptyStateIcon>
                  <TouchAppOutlinedIcon />
                </EmptyStateIcon>
                <EmptyStateTitle>Select a box to edit its reason</EmptyStateTitle>
                <HelperText>
                  Draw on an empty area to add a box. Drag an existing box to move it, or pull a handle to resize it. Nothing changes in the file
                  until you Save.
                </HelperText>
              </EmptyStateCard>
            </>
          )}
          <RailDivider />
          <RailSectionTitle>On this page ({onThisPage.length})</RailSectionTitle>
          <BoxList data-testid="page-box-list">
            {onThisPage.map(box => (
              <BoxListItem key={box.id}>
                <BoxListButton type="button" selected={box.id === selectedId} onClick={() => setSelectedId(box.id)}>
                  <LegendSwatch
                    fill={box.origin === 'manual' ? manualFill : AI_BOX_FILL}
                    stroke={box.origin === 'manual' ? theme.colors.primary : AI_BOX_STROKE}
                  />
                  <BoxReason>{box.reason || 'No reason yet'}</BoxReason>
                </BoxListButton>
              </BoxListItem>
            ))}
            {onThisPage.length === 0 && <HelperText>No redactions on this page.</HelperText>}
          </BoxList>
          <FileSummaryCard data-testid="file-summary">
            <strong>This file</strong>
            {suggestedCount} suggested · {manualCount} manual
            {dirty ? ` · ${deletedSinceSave} deleted since last save` : lastSaved ? ` · last saved ${lastSaved}` : ''}
          </FileSummaryCard>
        </Rail>
      </Body>

      <RedactAllOccurrencesDialog
        open={redactAll.isOpen}
        action={redactAll.action}
        categoryName={category.name}
        requestScope={redactAll.requestScope}
        initialText={redactAll.prefill.text}
        initialReason={redactAll.prefill.reason}
        busy={redactAll.redacting}
        error={redactAll.error}
        onCancel={redactAll.closeDialog}
        onConfirm={redactAll.confirm}
      />

      <UnsavedChangesDialog
        open={guard.guardOpen}
        editCount={Math.max(editCount, 1)}
        saving={saving && guard.isSavingToContinue()}
        onKeepEditing={guard.keepEditing}
        onDiscard={guard.discard}
        onSaveAndContinue={guard.saveAndContinue}
      />
    </Shell>
  )
}

RedactionEditorPage.propTypes = {
  pollIntervalMs: PropTypes.number
}

export default RedactionEditorPage
